import { useEffect, useState } from 'react';

import { Button, Stack, Typography } from '@mui/material';
import { DisplaySurveyType } from 'posthog-js';
import { usePostHog } from 'posthog-js/react';
import { useTranslation } from 'react-i18next';

import { SectionBox } from '../SectionBox';
import { Icon } from '@/components/ui/Icon';
import { uiIcons } from '@/constants/uiIcons';

interface UserSurveyCTAProps {
  surveyId: string;
  heading?: string;
  content?: string;
}

export const UserSurveyCTA = ({
  surveyId,
  heading,
  content,
}: UserSurveyCTAProps) => {
  const { t } = useTranslation('ui');
  const posthog = usePostHog();

  const [canShowSurvey, setCanShowSurvey] = useState(false);

  useEffect(() => {
    posthog.onSurveysLoaded(() => {
      // This checks cohort, completion, and all conditions
      posthog.getActiveMatchingSurveys((surveys) => {
        const hasSurvey = surveys.some((s) => s.id === surveyId);
        setCanShowSurvey(hasSurvey);
      });
    });
  }, [posthog, surveyId]);

  const handleShowSurvey = () => {
    posthog.displaySurvey(surveyId, {
      displayType: DisplaySurveyType.Popover,
      ignoreConditions: false,
      ignoreDelay: true,
    });
  };

  if (!canShowSurvey) {
    return null;
  }
  return (
    <SectionBox variant="outlined">
      <Stack gap={3} justifyContent="center" alignItems="center">
        <Icon name={uiIcons.content.feedback} color="secondary" size="large" />
        {heading && (
          <Typography
            component="h2"
            variant="bodyRegularMono"
            color="secondary"
          >
            {heading}
          </Typography>
        )}
        {content && <Typography align="center">{content}</Typography>}

        {!heading && !content && (
          <Typography component="h2" variant="h4" align="center">
            {content || t('userSurveyCTA.DefaultContent')}
          </Typography>
        )}

        <Button
          variant="outlined"
          color="secondary"
          onClick={handleShowSurvey}
          endIcon={<Icon name={uiIcons.navigation.openExternal} />}
          rounded
        >
          {t('userSurveyCTA.ButtonText')}
        </Button>
      </Stack>
    </SectionBox>
  );
};
